import { FlexColumn, FlexRow, MaterialIcon, useElementWidth } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import moment from 'moment';
import { forwardRef, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Rnd, RndDragCallback, RndResizeCallback } from 'react-rnd';
import { useDebounceValue } from 'usehooks-ts';
import { CAPTURE_DURATION_CONVERSION_RATE, CAPTURE_DURATION_LABEL, ZOOM_LEVELS } from '../../../../constants/streaming';
import { useStyles } from '../../../../hooks/useStyles';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import { CaptureDuration, CaptureMode, StreamingTimelineEvent, TimelineChangeEvent } from '../../../../types/streaming';
import { getMinutesAndSecondsDifference } from '../../../../utils/dates';
import CapturingVideoTimelineEvent from './CapturingVideoTimelineEvent';

type Props = {
  onChange: (range: TimelineChangeEvent) => void;
  mode: CaptureMode;
  duration: CaptureDuration;
  day: number;
  availableTracks?: { startDate: number; endDate: number }[];
  startDate?: number;
  endDate?: number;
  selectedEventDate?: number;
  selectedDate?: number;
  events?: StreamingTimelineEvent[];
};

function CapturingVideoTimeline({
  onChange,
  mode,
  duration,
  day,
  availableTracks,
  startDate,
  endDate,
  selectedEventDate,
  selectedDate,
  events,
}: Readonly<Props>) {
  const { theme } = useStyles();
  const { elementRef, elementWidth } = useElementWidth();
  const [persistingStartDate, setPersistingStartDate] = useState<number>();
  const [position, setPosition] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(0);
  const [lastZoom, setLastZoom] = useState<number>(0);
  const trackerRef = useRef<Rnd>(null);
  const zoomLevels = elementWidth > 1000 ? ZOOM_LEVELS : ZOOM_LEVELS.slice(2);
  const pixelsPerMinute = elementWidth / zoomLevels[zoom].durationMinutes;
  const pixelsPerSecond = pixelsPerMinute / 60;
  const timelineWidth = 24 * 60 * pixelsPerMinute;
  const minWidth =
    mode === CaptureMode.requesting ? pixelsPerMinute * 0.03 : pixelsPerMinute * zoomLevels[zoom].staticDuration;
  const maxWidth =
    mode === CaptureMode.requesting ? pixelsPerMinute * 5 : pixelsPerMinute * zoomLevels[zoom].staticDuration;
  const width =
    mode === CaptureMode.requesting
      ? pixelsPerMinute * CAPTURE_DURATION_CONVERSION_RATE[duration]
      : pixelsPerMinute * zoomLevels[zoom].staticDuration;
  const baseStartMinutes = position / pixelsPerMinute;
  const baseEndMinutes = (position + width) / pixelsPerMinute;
  const widthOffset = width / 2;
  const widthOffsetMinutes = widthOffset / pixelsPerMinute;
  const startMinutes = mode !== CaptureMode.requesting ? baseStartMinutes - widthOffsetMinutes : baseStartMinutes;
  const endMinutes = mode !== CaptureMode.requesting ? baseEndMinutes - widthOffsetMinutes : baseEndMinutes;
  const [debouncedStartMinutes] = useDebounceValue(startMinutes, 80);
  const [debouncedEndMinutes] = useDebounceValue(endMinutes, 80);

  useEffect(() => {
    const start = moment(day).startOf('day').add(debouncedStartMinutes, 'minutes');
    const end = moment(day).startOf('day').add(debouncedEndMinutes, 'minutes');
    setPersistingStartDate(start.valueOf());
    onChange({
      start: start.valueOf(),
      end: end.valueOf(),
    });
  }, [debouncedStartMinutes, debouncedEndMinutes, day]);

  useEffect(() => {
    if (persistingStartDate) {
      const date = new Date(persistingStartDate);
      if (moment(date).isSame(moment(new Date(day)), 'day')) {
        setPosition((date.getHours() * 60 + date.getMinutes()) * pixelsPerMinute + date.getSeconds() * pixelsPerSecond);
      }
    }
  }, [zoom, elementWidth, pixelsPerSecond, pixelsPerMinute]);

  useUpdateEffect(() => {
    if (selectedEventDate) {
      moveTrackerIntoTime(selectedEventDate);
    }
  }, [selectedEventDate]);

  useUpdateEffect(() => {
    if (selectedDate) {
      moveTrackerIntoTime(selectedDate);
    }
  }, [selectedDate]);

  useLayoutEffect(() => {
    if (startDate && endDate) {
      setLastZoom(zoom);
      const { minutes } = getMinutesAndSecondsDifference(startDate, endDate);
      const optimalZoom = getOptimalZoom(minutes);
      if (optimalZoom > -1) {
        setPersistingStartDate(startDate);
        setZoom(optimalZoom);
        if (trackerRef.current) {
          const trackerElement = trackerRef.current.getSelfElement();
          setTimeout(() => {
            trackerElement?.scrollIntoView({ inline: 'start' });
          }, 0);
        }
      }
    } else {
      setZoom(lastZoom);
    }
  }, [startDate, endDate, trackerRef]);

  const moveTrackerIntoTime = (ts: number) => {
    const date = new Date(ts);
    if (moment(date).isSame(moment(new Date(day)), 'day')) {
      setPosition((date.getHours() * 60 + date.getMinutes()) * pixelsPerMinute + date.getSeconds() * pixelsPerSecond);
      if (trackerRef.current) {
        const trackerElement = trackerRef.current.getSelfElement();
        setTimeout(() => {
          trackerElement?.scrollIntoView({ inline: 'center' });
        }, 0);
      }
    }
  };

  const updateOnChange = (posX: number) => {
    setPosition(posX);
  };

  const handleDrag: RndDragCallback = (e, d) => {
    e.stopPropagation();
    const newX = Math.max(0, d.x);
    updateOnChange(newX);
  };

  const handleResize: RndResizeCallback = (e, __, ref, ___, pos) => {
    e.stopPropagation();
    const newX = Math.max(0, pos.x);
    updateOnChange(newX);
  };

  const getOptimalZoom = (duration: number) => {
    const optimalZoom = ZOOM_LEVELS.filter((zoom) => zoom.durationMinutes > duration).sort(
      (a, b) => a.durationMinutes - b.durationMinutes
    )[0];
    const zoomIndex = ZOOM_LEVELS.findIndex((zoom) => zoom.durationMinutes === optimalZoom.durationMinutes);
    return zoomIndex > -1 ? zoomIndex - 1 : zoomIndex;
  };

  const timeMarks = useMemo(() => {
    const pixelsPerSecond = pixelsPerMinute / 60;
    const durationSec = zoomLevels[zoom].durationMinutes * 60;

    let majorTickIntervalSec;
    let intermediateTickIntervalSec;
    let minorTickIntervalSec;

    // Define los intervalos según duración
    if (durationSec > 4 * 3600) {
      majorTickIntervalSec = 3600; // 1 hora
      intermediateTickIntervalSec = null; // No intermedios
      minorTickIntervalSec = 1800; // 30 minutos
    } else if (durationSec <= 4 * 3600 && durationSec > 3600) {
      majorTickIntervalSec = 1800; // 30 minutos
      intermediateTickIntervalSec = 900; // 15 minutos
      minorTickIntervalSec = null;
    } else if (durationSec <= 3600 && durationSec > 900) {
      majorTickIntervalSec = 900; // 15 minutos
      intermediateTickIntervalSec = 300; // 5 minutos
      minorTickIntervalSec = 60; // 1 minuto
    } else if (durationSec <= 900 && durationSec > 600) {
      majorTickIntervalSec = 300; // 5 minutos
      intermediateTickIntervalSec = 60; // 1 minuto
      minorTickIntervalSec = 30; // 30 sec
    } else if (durationSec <= 600 && durationSec > 300) {
      majorTickIntervalSec = 60; // 1 minuto
      intermediateTickIntervalSec = null; // 30 sec
      minorTickIntervalSec = 10; // 15 sec
    } else {
      // 5 minutos o menos
      majorTickIntervalSec = 60; // 1 minuto
      intermediateTickIntervalSec = null; // No intermediates TODO: might add tick/label?
      minorTickIntervalSec = 1; // 1 segundo
    }

    const marks = [];

    for (
      let i = 0;
      i <= 24 * 60 * 60;
      i += Math.min(majorTickIntervalSec || intermediateTickIntervalSec || minorTickIntervalSec || 1, 60)
    ) {
      const isMajor = majorTickIntervalSec && i % majorTickIntervalSec === 0;
      const isIntermediate = intermediateTickIntervalSec && !isMajor && i % intermediateTickIntervalSec === 0;
      const isMinor = minorTickIntervalSec && !isMajor && !isIntermediate && i % minorTickIntervalSec === 0;

      if (isMajor) {
        marks.push(
          <div
            key={`major-${i}`}
            style={{
              position: 'absolute',
              left: i * pixelsPerSecond,
              bottom: 0,
              width: 2,
              height: 20,
              backgroundColor: '#666',
            }}
          />
        );
        // Label para major tick
        marks.push(
          <div
            key={`label-major-${i}`}
            style={{
              position: 'absolute',
              left: i * pixelsPerSecond,
              bottom: 22,
              transform: 'translateX(-50%)',
              fontSize: 10,
              color: '#333',
              whiteSpace: 'nowrap',
            }}
          >
            {moment().startOf('day').add(i, 'seconds').format('HH:mm')}
          </div>
        );
      } else if (isIntermediate) {
        marks.push(
          <div
            key={`intermediate-${i}`}
            style={{
              position: 'absolute',
              left: i * pixelsPerSecond,
              bottom: 0,
              width: 1,
              height: 14,
              backgroundColor: '#999',
            }}
          />
        );
        // Label para intermediate tick
        marks.push(
          <div
            key={`label-intermediate-${i}`}
            style={{
              position: 'absolute',
              left: i * pixelsPerSecond,
              bottom: 18,
              transform: 'translateX(-50%)',
              fontSize: 8,
              color: '#666',
              whiteSpace: 'nowrap',
            }}
          >
            {moment().startOf('day').add(i, 'seconds').format('HH:mm')}
          </div>
        );
      } else if (isMinor) {
        marks.push(
          <div
            key={`minor-${i}`}
            style={{
              position: 'absolute',
              left: i * pixelsPerSecond,
              bottom: 0,
              width: 1,
              height: 8,
              backgroundColor: '#ccc',
            }}
          />
        );
      }
    }

    return marks;
  }, [zoom, pixelsPerMinute, zoomLevels]);

  const onZoomIn = () => {
    setZoom((z) => {
      const newZoom = Math.min(zoomLevels.length - 1, z + 1);
      setLastZoom(newZoom);
      return newZoom;
    });
  };

  const onZoomOut = () => {
    setZoom((z) => {
      const newZoom = Math.max(0, z - 1);
      setLastZoom(newZoom);
      return newZoom;
    });
  };

  return (
    <FlexRow centered>
      <FlexColumn gap={theme.sizing.scale100}>
        <Button
          size="mini"
          shape="circle"
          kind="secondary"
          onClick={onZoomIn}
          style={{ fontSize: '16px', paddingBottom: '2px' }}
          disabled={zoom === zoomLevels.length - 1}
        >
          +
        </Button>
        <Button
          size="mini"
          shape="circle"
          kind="secondary"
          onClick={onZoomOut}
          style={{ fontSize: '16px', paddingBottom: '2px' }}
          disabled={zoom === 0}
        >
          -
        </Button>
      </FlexColumn>

      <div
        style={{
          width: '100%',
          border: '1px solid #ccc',
          borderRadius: '5px',
          overflowX: 'scroll',
          padding: '0 20px',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 80,
            userSelect: 'none',
          }}
          // onPointerDown={handlePointerDown}
          ref={elementRef}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: timelineWidth,
              height: '40px',
              borderBottom: '1px solid #ccc',
            }}
          >
            {timeMarks}
            {availableTracks?.map((track, index) => {
              const startDate = new Date(track.startDate);
              const offset =
                (startDate.getHours() * 60 + startDate.getMinutes()) * pixelsPerMinute +
                startDate.getSeconds() * pixelsPerSecond;
              const { minutes, seconds } = getMinutesAndSecondsDifference(track.startDate, track.endDate);
              const width = minutes * pixelsPerMinute + seconds * pixelsPerSecond;
              return (
                <div
                  key={index}
                  style={{
                    position: 'absolute',
                    top: '44px',
                    background: theme.colors.accent200,
                    left: offset,
                    height: '10px',
                    borderRadius: '5px',
                    width: width,
                  }}
                />
              );
            })}
            {events?.map((event, index) => {
              const startDate = new Date(event.created);
              const offset =
                (startDate.getHours() * 60 + startDate.getMinutes()) * pixelsPerMinute +
                startDate.getSeconds() * pixelsPerSecond;
              return (
                <CapturingVideoTimelineEvent
                  event={event}
                  key={index}
                  offset={offset}
                />
              );
            })}
            {mode !== CaptureMode.requesting && (
              <div
                style={{
                  position: 'absolute',
                  left: -widthOffset + 'px',
                  right: -widthOffset + 'px',
                  top: 0,
                  bottom: 0,
                }}
              >
                <DraggableTracker
                  ref={trackerRef}
                  duration={duration}
                  handleDrag={handleDrag}
                  handleResize={handleResize}
                  maxWidth={maxWidth}
                  minWidth={minWidth}
                  mode={mode}
                  position={position}
                  width={width}
                />
              </div>
            )}
            {mode === CaptureMode.requesting && (
              <DraggableTracker
                ref={trackerRef}
                duration={duration}
                handleDrag={handleDrag}
                handleResize={handleResize}
                maxWidth={maxWidth}
                minWidth={minWidth}
                mode={mode}
                position={position}
                width={width}
              />
            )}
          </div>
        </div>
      </div>
    </FlexRow>
  );
}

type DraggableTrackerProps = {
  position: number;
  width: number;
  handleDrag: RndDragCallback;
  handleResize: RndResizeCallback;
  minWidth: number;
  maxWidth: number;
  mode: CaptureMode;
  duration: CaptureDuration;
  ref: React.RefObject<Rnd>;
};

const DraggableTracker = memo(
  forwardRef<Rnd, Readonly<DraggableTrackerProps>>(function DraggableTrackerInner(
    { position, width, handleDrag, handleResize, minWidth, maxWidth, mode, duration },
    ref
  ) {
    return (
      <Rnd
        ref={ref}
        default={{
          x: position,
          y: 30,
          width: width,
          height: 20,
        }}
        position={{
          x: position,
          y: 30,
        }}
        onDrag={handleDrag}
        onResize={handleResize}
        enableResizing={false}
        disableDragging={false}
        dragAxis="x"
        dragGrid={[1, 0]}
        bounds="parent"
        minWidth={minWidth}
        maxWidth={maxWidth}
        style={{
          background: 'rgba(128, 90, 213, 0.3)',
          border: '1px solid #805ad5',
          borderRadius: 4,
          width: width + 'px',
        }}
        size={{
          width: width,
          height: 20,
        }}
      >
        {mode === CaptureMode.requesting && (
          <div
            style={{
              position: 'absolute',
              bottom: -25,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: 12,
              backgroundColor: '#805ad5',
              color: 'white',
              padding: '2px 4px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
            }}
          >
            {CAPTURE_DURATION_LABEL[duration]}
          </div>
        )}

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <MaterialIcon
            name={mode === CaptureMode.requesting ? 'videocam' : 'place'}
            size={'12px'}
          />
        </div>
      </Rnd>
    );
  })
);

export default CapturingVideoTimeline;
